import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	LineController,
	BarController,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Link } from "react-router-dom";
import SelectField from "../inputs/select";
import Button from "../inputs/button";
import Wrapper from "./wrapper";
import Loader from "../misc/loader";
import { getTagPageViewTrends } from "../../services/tag";
import { getSession } from "../../services/auth";
import { htmlLegendPlugin } from "./chartLegend";
import {
	RANGE_OPTIONS,
	DEFAULT_RANGE,
	sliceRange,
	subheadingFor,
	formatDayLabel,
	formatDayTitle,
} from "./pageViewTrend.helpers";
// CSS
import "./pageViewTrend.scss";

ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	LineController,
	BarController
);

const compact = Intl.NumberFormat("en-US", { notation: "compact" });
const exact = Intl.NumberFormat("en-US");

// Series colors double as axis colors so each Y axis reads as "the navy one is the
// bars, the green one is the line" without relying on page views being the bigger
// number (they usually are, but the two come from different events and an AMP-only
// silo has no unique users at all).
const PAGE_VIEWS_COLOR = "#30316f";
const UNIQUE_USERS_COLOR = "#9aca3c";

/**
 * Daily Trend card on the Console (ASB-3567): page views per day
 * (bars, left axis) and unique users per day (line, right axis) for the past
 * 7 / 14 / 30 complete days, chosen from the dropdown in the card header.
 *
 * Page views are page + amp rows, the count billing charges on; unique users
 * are distinct arcids on the identity beacon. The API returns all 30 days and
 * the dropdown slices client-side, so switching range makes no request.
 */
function PageViewTrend() {
	const [tagId, setTagId] = useState(localStorage.getItem("activeSilo"));
	const [series, setSeries] = useState(null);
	const [range, setRange] = useState(DEFAULT_RANGE);
	const [loading, setLoading] = useState(true);
	// null | "missing" | "failed". A 404 is the API's "ArcTag not found" (unknown, or
	// not visible to this user); anything else is a real failure and must not be
	// dressed up as "not available", or an outage looks like a missing ArcTag.
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		if (!getSession() || !tagId) return;
		setLoading(true);
		setError(null);
		try {
			const response = await getTagPageViewTrends(tagId);
			setSeries(Array.isArray(response.data) ? response.data : []);
		} catch (err) {
			console.error("Page view trends unavailable", err);
			setSeries([]);
			setError(err?.status === 404 ? "missing" : "failed");
		}
		setLoading(false);
	}, [tagId]);

	useEffect(() => {
		const onStorage = () => setTagId(localStorage.getItem("activeSilo"));
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const visible = useMemo(() => sliceRange(series ?? [], range), [series, range]);
	const hasData = visible.some((day) => day.pageViews > 0 || day.uniqueUsers > 0);

	const data = {
		labels: visible.map((day) => formatDayLabel(day.date)),
		// Line first: Chart.js draws the last dataset first, so the line sits on
		// top of the bars, and the shared legend plugin reverses the order, so the
		// legend reads "Page Views, Unique Users".
		datasets: [
			{
				type: "line",
				label: "Unique Users",
				backgroundColor: UNIQUE_USERS_COLOR,
				borderColor: UNIQUE_USERS_COLOR,
				fill: false,
				data: visible.map((day) => day.uniqueUsers),
				yAxisID: "y1",
			},
			{
				type: "bar",
				label: "Page Views",
				barThickness: range > 14 ? 12 : 16,
				backgroundColor: PAGE_VIEWS_COLOR,
				borderRadius: 5,
				data: visible.map((day) => day.pageViews),
				yAxisID: "y",
			},
		],
	};

	const axis = (position, label, color) => ({
		type: "linear",
		display: true,
		position,
		min: 0,
		grid: { drawOnChartArea: false },
		border: { color },
		title: { display: true, text: label, color, font: { weight: "bold" } },
		ticks: {
			callback: (value) => compact.format(value),
			color,
			font: { weight: "bold" },
			maxTicksLimit: 6,
		},
	});

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: "index", intersect: false },
		plugins: {
			htmlLegend: { containerID: "legend-wrapper" },
			legend: { display: false },
			tooltip: {
				enabled: true,
				callbacks: {
					title: (items) => (items.length ? formatDayTitle(visible[items[0].dataIndex].date) : ""),
					label: (context) => `${context.dataset.label}: ${exact.format(context.parsed.y)}`,
				},
			},
		},
		layout: { padding: { top: 32, left: 16, right: 16 } },
		scales: {
			y: axis("left", "Page Views", PAGE_VIEWS_COLOR),
			y1: axis("right", "Unique Users", UNIQUE_USERS_COLOR),
			x: {
				grid: { drawOnChartArea: false },
				ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: range > 14 ? 10 : range },
			},
		},
	};

	// The shared SelectField so this looks like every other dropdown in AMS (square
	// corners, green theme, check-mark on the selected option); pageViewTrend.scss
	// trims its form-sized height for the card header. The check-mark is the
	// selected-range indicator; no extra bolding.
	const rangeSelect = (
		<SelectField
			name="pageViewRange"
			customClasses="range-select"
			width="short"
			required={false}
			disabled={false}
			search={false}
			options={RANGE_OPTIONS}
			selected={RANGE_OPTIONS.find((option) => option.value === range)}
			onSubmit={(option) => option && setRange(option.value)}
		/>
	);

	let body;
	if (loading) {
		body = <Loader size="xl" />;
	} else if (error === "missing") {
		body = <p className="p3 chart-message">Page views are not available for this ArcTag.</p>;
	} else if (error) {
		body = (
			<div className="chart-message">
				<p className="p3">Page views could not be loaded.</p>
				<Button
					text="Try again"
					type="button"
					style="light"
					size="sm"
					disabled={false}
					loaded={false}
					onClick={load}
				/>
			</div>
		);
	} else if (!hasData) {
		body = <p className="p3 chart-message">No page views recorded in the past {range} days.</p>;
	} else {
		body = (
			<Chart
				key={range}
				type="bar"
				data={data}
				options={options}
				plugins={[htmlLegendPlugin]}
			/>
		);
	}

	return (
		<Wrapper
			heading={<span className="p3 strong">Daily Trend</span>}
			subheading={subheadingFor(range)}
			action={rangeSelect}
			padded={false}
		>
			<div className="chart-wrapper">
				<div className="chart-body">{body}</div>
				<div id="chart-footer">
					<div id="legend-wrapper"></div>
					<Link to="/audience-insights/discovery">View more insights</Link>
				</div>
			</div>
		</Wrapper>
	);
}

export default PageViewTrend;
